<template>
  <div class='cloud-account-row'>
    <div class='cloud-account-row__select'>
      <div class='check-box'>
        <custom-checkbox
          color="cold-marine"
          :value="isSelected"
          size="s"
          @input="$emit('checked', item)"
        />
      </div>
    </div>
    <div class='cloud-account-row__content'>
      <div class="main-info" @click="isOpen = !isOpen">
        <div class='cloud-account-row__content__actions' :class="{ successful: !isVulnerable }">
          <span
            class="material-icons-round logo"
            :class="{ successful: !isVulnerable }"
            :style="`color: ${vulnerabilityIcon.color}`"
            v-if='!isVulnerable'
          >
            {{ vulnerabilityIcon.iconName }}
          </span>
          <custom-tooltip popper-class="light-tooltip" v-else>
            <div slot='buttonContent'>
              <span class="material-icons-round logo" :style="`color: ${vulnerabilityIcon.color}`"  @click='goToIssues'>
                {{ vulnerabilityIcon.iconName }}
              </span>
            </div>
            <div slot="content">
              <slot name="tooltip">
                {{ warningTooltip }}
              </slot>
            </div>
          </custom-tooltip>
        </div>
        <div class="url">
          <div class="url__data">
            <custom-tooltip class="url__data__tooltip" placement="right" popper-class="light-tooltip">
              <div slot='buttonContent'>
                {{ url }}
              </div>
              <div slot="content">
                <slot name="tooltip">
                  {{ url }}
                </slot>
              </div>
            </custom-tooltip>
          </div>
          <div class='url__data__icon' v-if='isAccountSelected'>
            <v-icon
              x-small
            >
              radio_button_checked
            </v-icon>
          </div>
        </div>
        <div class="type">
          <div class='type__logo'>
            <img  v-if="logo" :src="logo" />
          </div>
        </div>
        <div class='right-section'>
          <div class='add-test-card'>
            <custom-tooltip placement="right" popper-class="light-tooltip" v-if="!isOpen">
              <div slot='buttonContent'>
                <v-btn class="add-test-btn" @click.stop="openAddTestPopup"  :ripple="false" :disabled='addTestCardDisabled'>
                  <v-icon class="add-test-icon-no-txt">add</v-icon>
                </v-btn>
              </div>
              <div slot="content">
                <slot name="tooltip">
                  {{ toolTipText }}
                </slot>
              </div>
            </custom-tooltip>
          </div>
          <div class='arrow-expand-section' v-if='displayOpenedRow'>
            <v-icon class="expand-icon" :class="{ expand: isOpen }">expand_more</v-icon>
          </div>
        </div>
      </div>
      <div class="test-container" v-if="isOpen">
        <test-card
          v-for="(testCard, key) in testCardsForDisplay"
          :test-card="testCard"
          :item="testCard.url"
          :authentication="getAuthenticationForTestCard(testCard)"
          :key="key"
        />
        <div v-if='!addTestCardDisabled' class="add-test-area" v-ripple="{ class: `blue--text` }" @click="openAddTestPopup">
          <v-icon class="add-test-icon">add</v-icon>
          <span class="add-test-btn-txt">{{ $t('configuration.add.test') }}</span>
        </div>
        <div class='test-card-display-toggle' v-if='(testCardsForCloudAccount || []).length > amountOfTestCardInRow'>
          <span text class='toggle-test-card-btn' @click.stop="displayAllTestCards = !displayAllTestCards">
            {{ displayTestCardsButtonLabel }}
          </span>
        </div>
      </div>
      <div class='cloud-account-row__content__details'>
        <div class='cloud-account-row__content__details__organization'>
          <v-chip
            label
            outlined
            pill
            text-color='#657D94'
            small
            class='tag'
          >
             <span class="material-icons-outlined">
                business
            </span>
            <span class='organization-name'>
              {{organization}}
            </span>
          </v-chip>
        </div>
        <div class='cloud-account-row__content__details__tags'>
          <div class='cloud-account-row__content__details__tags__tags-list'>
          <v-chip
            :ripple='false'
            label
            outlined
            close
            pill
            text-color='#2459d5'
            small
            class='tag'
            v-for='(tagKey, index) in nonExcludedTagKeys'
            :key='index'
            close-icon='close'
            @click:close='removeTagFromList(tagKey)'
          >
            {{ tagKey }}
          </v-chip>
        </div>
          <span class='no-tags' v-if='tagKeys.length === 0'>
            {{ $t('no.tags') }}
          </span>
          <span class="material-icons" @click='openAddTagPopup'>
            local_offer
          </span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import CustomCheckbox from '@/components/common/CustomCheckbox.vue';
import { views } from '@/router/constants';
import TestCard from '@/components/configuration/TestCard.vue';
import CustomTooltip from '@/components/common/CustomTooltip.vue';
import moment from 'moment/moment';
import { CONFIGURATION, CONFIGURATION_ACTION_TYPES } from '@/constants/configuration';
import awsAuthenticatedIcon from '@/assets/aws_authenticated_icon.svg';
import awsIcon from '@/assets/aws.svg';
import gcpAuthenticatedIcon from '@/assets/gcp_authenticated_icon.svg';
import gcpIcon from '@/assets/gcp.svg';
import azureAuthenticatedIcon from '@/assets/azure_authenticated_icon.svg';
import azureIcon from '@/assets/azure.svg';
import { mapGetters } from 'vuex';
import { SET_DOMAIN_FILTER } from '@/store/filters/actionTypes';

export default {
  name: 'CloudAccountRow',
  components: { CustomTooltip, TestCard, CustomCheckbox },
  props: {
    addTestCardDisabled: {
      type: Boolean,
      default: false,
      required: false,
    },
    amountOfTestCardInRow: {
      type: Number,
      default: 5,
      required: false,
    },
    item: {
      type: Object,
      required: true,
    },
    isSelected: {
      type: Boolean,
      default: () => false,
    },
  },
  data() {
    return {
      displayAllTestCards: false,
      isOpen: false,
      addTestPopup: false,
      showPopup: false,
    };
  },
  computed: {
    ...mapGetters(['cloudAccountsTags', 'organizationsTags', 'domainFilter']),
    isAccountSelected() {
      return this.domainFilter.includes(this.url);
    },
    url() {
      return this.item.url;
    },
    logo() {
      if (this.item.type) {
        if (this.item.type === CONFIGURATION.SOURCE_TYPE.aws) {
          if (this.item.authenticated) {
            return awsAuthenticatedIcon;
          }
          return awsIcon;
        }
        if (this.item.type === CONFIGURATION.SOURCE_TYPE.gcp) {
          if (this.item.authenticated) {
            return gcpAuthenticatedIcon;
          }
          return gcpIcon;
        }
        if (this.item.type === CONFIGURATION.SOURCE_TYPE.azure) {
          if (this.item.authenticated) {
            return azureAuthenticatedIcon;
          }
          return azureIcon;
        }
      }
      return null;
    },
    toolTipText() {
      return this.addTestCardDisabled ? this.$t('license.disable.above.3.scans') : this.$t('configuration.add.test');
    },
    warningTooltip() {
      return this.$t('configuration.vulnerable.asset.tooltip');
    },
    isVulnerable() {
      return this.item.vulnerable;
    },
    vulnerabilityIcon() {
      if (this.isVulnerable) {
        return { iconName: 'warning', color: '#E0255E' };
      }
      return { iconName: 'check_circle', color: '#2baf69' };
    },
    displayTestCardsButtonLabel() {
      return this.displayAllTestCards ? this.$t('hide.test.cards') : this.$t('show.all.test.cards');
    },
    organization() {
      return this.item.organization?.name || '';
    },
    testCardsForCloudAccount() {
      return [...this.item.testCards].sort((a, b) => (moment(a.createdDate).isAfter(moment(b.createdDate)) ? -1 : 1));
    },
    testCardsForDisplay() {
      const maxPossibleTestCards = this.testCardsForCloudAccount?.length < this.amountOfTestCardInRow ? this.testCardsForCloudAccount?.length : this.amountOfTestCardInRow;
      return this.displayAllTestCards ? this.testCardsForCloudAccount : this.testCardsForCloudAccount.slice(0, maxPossibleTestCards);
    },
    displayOpenedRow() {
      return this.testCardsForCloudAccount?.length;
    },
    nonExcludedTagKeys() {
      return this.tagKeys.filter(key => this.tags[key] !== 'exclude').sort();
    },
    tagKeys() {
      return Object.keys(this.tags);
    },
    currentOrganizationsTags() {
      return this.organizationsTags?.[this.organization] || {};
    },
    currentOrganizationTagKeys() {
      return Object.keys(this.currentOrganizationsTags);
    },
    tags() {
      return {...this.currentOrganizationsTags, ...this.currentCloudAccountsTags };
    },
    currentCloudAccountsTags() {
      return this.cloudAccountsTags?.[this.url] || {};
    },
    currentCloudAccountsTagKeys() {
      return Object.keys(this.currentCloudAccountsTags);
    },
  },
  methods: {
    getAuthenticationForTestCard(testCard) {
      // Check if test card has authentication object stored in metadata
      if (testCard.metaData?.authentication?.authenticationObject) {
        const authObj = testCard.metaData.authentication.authenticationObject;
        return {
          keyName: authObj.settingsMetaData?.name || 'Unnamed Key',
          keyType: authObj.settingsMetaData?.type,
          hasKey: true,
          credentialId: authObj.id,
        };
      }
      return this.item.authentication;
    },
    async removeTagFromList(tagKey) {
      const tagsUpdated = {...this.currentCloudAccountsTags};
      if (this.currentOrganizationTagKeys.includes(tagKey)) {
        tagsUpdated[tagKey] = 'exclude';
      } else if (this.currentCloudAccountsTagKeys.includes(tagKey)) {
        delete tagsUpdated[tagKey];
      }

      await this.$emit('submitTagsInformation', {
        action: CONFIGURATION_ACTION_TYPES.REMOVE_TAG,
        configType: CONFIGURATION.CONFIG_TYPES.TAGS_CLOUD_ACCOUNT,
        items: [{
          ...this.item,
          url: this.url,
          tld: this.item.tld,
          tags: { ...tagsUpdated },
        }],
      });
    },
    openAddTagPopup() {
      this.$emit('openTagsPopup', [this.item]);
    },
    async openAddTestPopup() {
      await this.$emit('openAddTestPopup', {
        item: this.item,
      });
    },
    async goToIssues() {
      await this.$store.dispatch(SET_DOMAIN_FILTER, [this.item.url]);
      await this.$router.push({ name: views.ISSUES, params: { tld: this.item.url }});
    },
  },
};
</script>

<style lang="scss">
@import '~@/assets/styles/design-system';

.cloud-account-row {
  // Vuetify chip overrides - unscoped for child component styling
  .v-chip.v-chip.v-chip {
    background-color: var(--bg-tertiary);
    border-color: transparent;
    padding: $space-2;
  }

  .v-chip.v-chip--outlined.v-chip.v-chip {
    background-color: var(--bg-tertiary);
    border-color: transparent;
    padding: $space-1;
    font-size: $font-size-sm;
  }

  .v-chip.v-chip--outlined .v-icon {
    font-size: $font-size-base;
    color: var(--text-primary);
  }

  .v-chip .v-chip__close.v-icon.v-icon--right {
    font-size: $font-size-base;
    color: var(--text-primary);
  }

  .v-btn--disabled.v-btn--disabled {
    background-color: rgba(179, 49, 207, 0.3);
  }
}
</style>

<style lang='scss' scoped>
@import '~@/assets/styles/design-system';

#app .cloud-account-row {
  .add-test-btn {
    background-color: var(--btn-action-bg) !important;
    text-align: center;
    box-shadow: none;
    min-width: rem(28);
    max-width: rem(28);
    min-height: rem(28);
    max-height: rem(28);
    user-select: none;
    color: var(--btn-action-border-color) !important;

    .v-icon {
      font-size: $font-size-lg;
      color: var(--btn-action-border-color) !important;
    }

    &:hover {
      cursor: pointer;
      border: 1px solid var(--btn-action-border-color);
      background-color: var(--btn-action-hover-bg) !important;
    }

    &:active {
      border: 1px solid var(--btn-action-border-color);
      background-color: var(--btn-action-hover-bg) !important;
    }

    &::before {
      color: transparent;
    }
  }
}

.cloud-account-row {
  min-height: rem(58);
  border-bottom: 1px solid var(--border-primary);
  display: flex;
  padding: $space-1 0;

  &__open {
    padding-top: $space-0-5;
    z-index: 4;
  }

  &__select {
    margin-top: rem(9.5);
  }

  &__content {
    width: 100%;
    display: flex;
    flex-direction: column;

    &__actions {
      display: flex;
      align-items: center;
      margin-right: $space-1;

      .more-btn {
        opacity: 0;
        color: var(--text-muted);

        &:hover {
          cursor: pointer;
          opacity: 100;
        }
      }

      .material-icons-round {
        margin-right: $space-1;

        &.successful {
          &:hover {
            cursor: auto;
          }
        }
      }
    }

    &__details {
      width: 100%;
      margin-top: $space-4;
      display: flex;
      justify-content: space-between;
      align-items: center;

      &__tags {
        display: flex;
        align-self: end;
        align-items: center;
        margin-top: 0.5%;
        margin-bottom: 0.5%;

        &__tags-list {
          margin-right: $space-2;

          .tag {
            margin-right: $space-2;
          }
        }

        .no-tags {
          margin-right: $space-1;
          color: var(--text-muted);
          font-family: $default-application-font-regular;
          font-size: $font-size-sm;
          letter-spacing: 1.1px;
          line-height: rem(15);
        }

        .material-icons {
          color: var(--text-secondary);
          font-size: $font-size-lg;

          &:hover {
            cursor: pointer;
            color: var(--brand-secondary);
          }
        }
      }

      &__organization {
        display: flex;
        align-self: start;
        justify-self: start;
        align-items: center;

        .organization-name {
          text-transform: capitalize;
          font-size: $font-size-xs;
        }

        .material-icons-outlined {
          color: var(--text-secondary);
          font-size: $font-size-base;
          margin-right: $space-1;
        }
      }
    }

    .test-container {
      .add-test-area {
        .add-test-icon {
          // Global .v-icon override wins over scoped styles — force brand color
          color: var(--brand-primary) !important;
          font-size: $font-size-2xl;
          margin-right: $space-1;

          &-no-txt {
            margin-right: 0;
          }
        }

        .add-test-btn-txt {
          background-color: transparent;
          box-shadow: none;
          color: var(--brand-primary);
          font-family: $default-application-font-medium;
          font-size: rem(13);
          letter-spacing: 1.1px;
          line-height: $space-4;
          text-transform: uppercase;
        }
      }
    }

    .main-info {
      align-items: center;
      display: flex;

      .url {
        align-items: center;
        color: var(--text-primary);
        cursor: pointer;
        display: flex;
        width: 35%;
        font-family: $default-application-font-medium;
        font-size: $font-size-base;
        line-height: $space-5;
        height: $space-8;
        margin-right: 2.5%;
        flex-direction: row;
        justify-content: space-between;

        &__data {
          width: 90%;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;

          &__tooltip {
            color: var(--text-primary);
            background-color: transparent;
          }

          &__icon {
            position: absolute;
            left: 18%;

            .v-icon {
              color: var(--severity-info);
            }
          }
        }
      }

      .type {
        width: 20%;

        &__logo {
          max-width: rem(28);
          margin-top: $space-2;
          margin-right: $space-2;

          img {
            width: rem(28);
          }
        }

        .label {
          height: $space-5;
          width: 22%;
          color: var(--text-primary);
          font-family: $default-application-old-font-regular;
          font-size: $font-size-base;
          letter-spacing: 0;
          line-height: $space-5;

          &.colorBoolean {
            height: $space-5;
            width: rem(54);
            color: var(--severity-critical);
            font-family: $default-application-old-font-regular;
            font-size: $font-size-base;
            font-weight: 600;
            letter-spacing: 0;
            line-height: $space-5;
          }
        }
      }

      .right-section {
        display: flex;
        width: 3%;
        position: absolute;
        right: 2.5%;

        .add-test-card {
          display: flex;
          justify-content: flex-end;
          flex: auto;
          flex-direction: row;
          width: rem(150);

          :deep(.v-btn:not(.v-btn--round).v-size--default) {
            background-color: var(--btn-action-bg) !important;
            padding: $space-2;
            margin-left: $space-4;
          }

          .add-test-icon {
            // Global .v-icon override wins over scoped styles — force brand color
            color: var(--brand-primary) !important;
            font-size: $font-size-2xl;
            margin-right: $space-1;

            &-no-txt {
              margin-right: 0;
            }
          }

          .add-test-btn {
            background-color: var(--btn-action-bg) !important;
            text-align: center;
            box-shadow: none;
            min-width: rem(28);
            max-width: rem(28);
            min-height: rem(28);
            max-height: rem(28);
            user-select: none;
            color: var(--btn-action-border-color) !important;

            .v-icon {
              font-size: $font-size-lg;
              color: var(--btn-action-border-color) !important;
            }

            &:hover {
              cursor: pointer;
              border: 1px solid var(--btn-action-border-color);
              background-color: var(--btn-action-hover-bg) !important;
            }

            &:active {
              border: 1px solid var(--btn-action-border-color);
              background-color: var(--btn-action-hover-bg) !important;
            }

            &::before {
              color: transparent;
            }
          }
        }

        .arrow-expand-section {
          display: flex;
          justify-content: flex-end;
        }
      }
    }
  }
}
</style>
